import React, { useState, useEffect } from 'react';
import { View, Text, StyleSheet, ScrollView, Platform, Pressable } from 'react-native';
import { useSelector } from 'react-redux';
import Svg, { Path } from 'react-native-svg';
import { getSessionLockService } from '../../../../api/sessionLockService';
import { DLOLaborOptimizationBreadcrumb } from '../../components/DLOBreadcrumb';
import DLOTabNavigation from '../../components/DLOTabNavigation';
import DLOModal from '../../components/DLOModal';
import { RootState, useAppDispatch } from '../../../../store';
import { resetScheduleSettingUpdateState, resetScheduleSettingData, setScheduleForecastError } from '../../../../store/slices/dloSlice';
import { selectActiveLocks } from '../../../../store/slices/sessionLockSlice';
import { 
  selectForecastDSDUpdateLoading, 
  selectForecastNONDSDUpdateLoading, 
  selectActualDSDUpdateLoading, 
  selectActualNONDSDUpdateLoading, 
  selectForecastDSDZoneUpdateLoading, 
  selectForecastNONDSDZoneUpdateLoading, 
  selectActualDSDZoneUpdateLoading, 
  selectActualNONDSDZoneUpdateLoading, 
  selectForecastDSDZoneSaved, 
  selectForecastNONDSDZoneSaved, 
  selectActualDSDZoneSaved, 
  selectActualNONDSDZoneSaved, 
  selectScheduleSettingsHistoricalHoursLoading, 
  selectHistoricalHoursUpdateLoading, 
  selectShiftTimesLoading, 
  selectShiftTimesUpdateLoading, 
  selectScheduleForecastLoading,
  selectScheduleForecastError,
  selectCreateSchedulePeriod,
  selectCreateScheduleWeek,
  selectCreateScheduleYear
} from '../../../../store/slices/dloSlice';

// Local imports
import { useDLOScheduleData } from './hooks/useDLOScheduleData';
import { useDLOScheduleHandlers } from './hooks/useDLOScheduleHandlers';
import { SCHEDULE_SETTING_TABS } from './constants/tabConfig';
import { DLOScheduleSettingProps, ModalState } from './types';
import ForecastedVolumeTab from './components/ForecastedVolumeTab';
import ActualVolumeTab from './components/ActualVolumeTab';
import { DLOButton } from '../../components';
import { getPeriodWeekYearDisplay } from '../DLOCreateSchedule/utils/scheduleHelpers';
import { useAutoExtendLock } from '../../hooks/useAutoExtendLock';

// File upload icon component
const FileUploadIcon = ({ color = '#FFFFFF', size = 20 }) => (
  <Svg width={size} height={size} viewBox="0 0 20 20" fill="none">
    <Path d="M7.49996 13.3333H12.5V8.33333H15.8333L9.99996 2.5L4.16663 8.33333H7.49996V13.3333ZM9.99996 4.85833L11.8083 6.66667H10.8333V11.6667H9.16663V6.66667H8.19163L9.99996 4.85833ZM4.16663 15H15.8333V16.6667H4.16663V15Z" fill={color} />
  </Svg>
);

const DLOScheduleSetting: React.FC<DLOScheduleSettingProps> = ({ onNavigate, activeTab: initialActiveTab, scheduleType = 'new_schedule' }) => {
  const [activeTab, setActiveTab] = useState(initialActiveTab || 'forecasted');
  const [modalState, setModalState] = useState<ModalState>({
    visible: false,
    title: '',
    message: '',
    type: 'success'
  });
  
  const dispatch = useAppDispatch();
  
  // Auto-extend session lock while user is actively working
  useAutoExtendLock();

  // Use custom hooks
  const {
    selectedLocation,
    dloScheduleSetting,
    forecastDSDTableData,
    forecastNONDSDTableData,
    actualDSDTableData,
    actualNONDSDTableData,
    forecastDSDZoneTableData,
    forecastNONDSDZoneTableData,
    actualDSDZoneTableData,
    actualNONDSDZoneTableData,
    historicalHoursTableData,
    historicalHoursData,
    shiftTimesData,
    shiftTimesTableData,
    scheduleForecastData,
    scheduleForecastTableData,
    cleanScheduleSettingsData,
    cleanScheduleSettingsLoading,
    createSchedulePeriod: hookCreateSchedulePeriod,
    createScheduleWeek: hookCreateScheduleWeek,
    createScheduleYear: hookCreateScheduleYear,
    isAllDataLoaded
  } = useDLOScheduleData(scheduleType);

  // Get loading states and saved flags using a single selector
  const {
    forecastDSDUpdateLoading,
    forecastNONDSDUpdateLoading,
    actualDSDUpdateLoading,
    actualNONDSDUpdateLoading,
    forecastDSDZoneUpdateLoading,
    forecastNONDSDZoneUpdateLoading,
    actualDSDZoneUpdateLoading,
    actualNONDSDZoneUpdateLoading,
    forecastDSDZoneSaved,
    forecastNONDSDZoneSaved,
    actualDSDZoneSaved,
    actualNONDSDZoneSaved,
    historicalHoursLoading,
    historicalHoursUpdateLoading,
    shiftTimesLoading,
    shiftTimesUpdateLoading,
    scheduleForecastLoading,
    scheduleForecastError,
    createSchedulePeriod,
    createScheduleWeek,
    createScheduleYear
  } = useSelector((state: RootState) => ({
    forecastDSDUpdateLoading: selectForecastDSDUpdateLoading(state),
    forecastNONDSDUpdateLoading: selectForecastNONDSDUpdateLoading(state),
    actualDSDUpdateLoading: selectActualDSDUpdateLoading(state),
    actualNONDSDUpdateLoading: selectActualNONDSDUpdateLoading(state),
    forecastDSDZoneUpdateLoading: selectForecastDSDZoneUpdateLoading(state),
    forecastNONDSDZoneUpdateLoading: selectForecastNONDSDZoneUpdateLoading(state),
    actualDSDZoneUpdateLoading: selectActualDSDZoneUpdateLoading(state),
    actualNONDSDZoneUpdateLoading: selectActualNONDSDZoneUpdateLoading(state),
    forecastDSDZoneSaved: selectForecastDSDZoneSaved(state),
    forecastNONDSDZoneSaved: selectForecastNONDSDZoneSaved(state),
    actualDSDZoneSaved: selectActualDSDZoneSaved(state),
    actualNONDSDZoneSaved: selectActualNONDSDZoneSaved(state),
    historicalHoursLoading: selectScheduleSettingsHistoricalHoursLoading(state),
    historicalHoursUpdateLoading: selectHistoricalHoursUpdateLoading(state),
    shiftTimesLoading: selectShiftTimesLoading(state),
    shiftTimesUpdateLoading: selectShiftTimesUpdateLoading(state),
    scheduleForecastLoading: selectScheduleForecastLoading(state),
    scheduleForecastError: selectScheduleForecastError(state),
    createSchedulePeriod: selectCreateSchedulePeriod(state),
    createScheduleWeek: selectCreateScheduleWeek(state),
    createScheduleYear: selectCreateScheduleYear(state),
  }));

  const periodWeekYearDisplay = getPeriodWeekYearDisplay(createSchedulePeriod, createScheduleWeek, createScheduleYear);

  // Get handlers
  const handlers = useDLOScheduleHandlers({
    forecastDSDTableData,
    forecastNONDSDTableData,
    actualDSDTableData,
    actualNONDSDTableData,
    forecastDSDZoneTableData,
    forecastNONDSDZoneTableData,
    actualDSDZoneTableData,
    actualNONDSDZoneTableData,
    historicalHoursTableData,
    selectedLocation,
    setModalState,
  });

  // Watch for update results and show modal (coordinated to prevent double popups)
  useEffect(() => {
    if (!dloScheduleSetting.updateLoading) {
      // Error takes priority over success
      if (dloScheduleSetting.error) {
        // setModalState({
        //   visible: true,
        //   type: 'error',
        //   title: 'Error',
        //   message: dloScheduleSetting.error
        // });
      } else if (dloScheduleSetting.updateSuccess) {
        setModalState({
          visible: true,
          type: 'success',
          title: 'Success',
          message: 'Schedule settings saved successfully'
        });
      }
    }
  }, [dloScheduleSetting.updateSuccess, dloScheduleSetting.error, dloScheduleSetting.updateLoading]);

  // Watch for cleanScheduleSettings completion and show modal if availability is "true"
  useEffect(() => {
    if (cleanScheduleSettingsData && !cleanScheduleSettingsLoading && cleanScheduleSettingsData.data?.availability == true) {
      const period = cleanScheduleSettingsData.data.period;
      const week = cleanScheduleSettingsData.data.week;
      const weekInPeriod = week - ((period - 1) * 4);
      setModalState({
        visible: true,
        type: 'success',
        title: 'Schedule Settings Found',
        message: `Schedule Settings for period ${period} week ${weekInPeriod} already exist. We have retained the existing settings.`
      });
    }
  }, [cleanScheduleSettingsData, cleanScheduleSettingsLoading]);

  // Watch for schedule forecast errors and show modal
  useEffect(() => {
    if (scheduleForecastError && !scheduleForecastLoading) {
      setModalState({
        visible: true,
        type: 'error',
        title: 'Error Loading Schedule Data',
        message: scheduleForecastError
      });
    }
  }, [scheduleForecastError, scheduleForecastLoading]);

  // Get active locks from Redux
  const activeLocks = useSelector(selectActiveLocks);

  // Cleanup: Reset schedule setting data when component unmounts
  useEffect(() => {
    return () => {
      dispatch(resetScheduleSettingData());
    };
  }, [dispatch]);

  // NOTE: We do NOT release locks on component unmount anymore
  // Locks are kept alive by heartbeat and should only be released when:
  // 1. User clicks "Go Back to Dashboard" (handleBackToDashboard) 
  // 2. Lock times out (auto-release handled by sessionLockService)
  // 3. User clicks "Go Back" in expiration warning modal
  // This allows navigating within DLO (e.g., to CreateSchedule) without losing the lock

  const handleTabChange = (tabId: string) => {
    setActiveTab(tabId);
    console.log('Tab changed to:', tabId);
  };

  const handleBackToDashboard = async () => {
    // CRITICAL: Get active locks from SERVICE, not Redux
    // Service has the LATEST refreshed tokens
    const lockService = getSessionLockService();
    if (lockService) {
      const serviceLocks = lockService.getActiveLocks();
      if (serviceLocks.size > 0) {
        console.log('[DLOScheduleSetting] Releasing locks on back to dashboard:', Array.from(serviceLocks.keys()));
        for (const [resourceId, lockData] of serviceLocks.entries()) {
          try {
            console.log('[DLOScheduleSetting] Releasing lock with CURRENT token:', lockData.lockToken?.substring(0, 20) + '...');
            await lockService.releaseLock(resourceId, lockData.lockToken);
          } catch (error) {
            console.error('[DLOScheduleSetting] Error releasing lock:', error);
          }
        }
      }
    }
    
    dispatch(resetScheduleSettingData());
    onNavigate?.('DLODashboard');
  };

  const handleBackToCreateSchedule = () => {
    dispatch(resetScheduleSettingData());
    onNavigate?.('DLOCreateSchedule');
  };

  const handleModalClose = () => {
    setModalState(prev => ({ ...prev, visible: false }));
    dispatch(resetScheduleSettingUpdateState());
    // Clear the schedule forecast error when modal closes
    dispatch(setScheduleForecastError(null));
  };

  return (
    <ScrollView style={styles.container}>
      {/* Breadcrumb Navigation */}
      <View style={styles.breadcrumbContainer}>
        <DLOLaborOptimizationBreadcrumb
          onHomePress={handleBackToDashboard}
          onLaborOptimizationPress={handleBackToDashboard}
          currentPage="Schedule Settings"
        />
      </View>
      {/* Content Area */}
      <View style={styles.content}>
        <View style={styles.headerContainer}>
          <Text style={styles.pageTitle}>Schedule Settings - {periodWeekYearDisplay}</Text>
          <View style={styles.actionsContainer}>
            <DLOButton
              text="Create Schedule"
              variant="primary"
              size="medium"
              iconLeading={<FileUploadIcon color="#FFFFFF" size={20} />}
              onPress={handleBackToCreateSchedule}
              disabled={!isAllDataLoaded}
            />
          </View>
        </View>
        
        {/* Tab Navigation */}
        <DLOTabNavigation
          tabs={SCHEDULE_SETTING_TABS}
          activeTabId={activeTab}
          onTabChange={handleTabChange}
          style={styles.tabNavigation}
        />

        {/* Tab Content */}
        <View style={styles.tabContent}>
          {activeTab === 'forecasted' ? (
            <ForecastedVolumeTab
              scheduleForecastData={scheduleForecastData}
              scheduleForecastTableData={scheduleForecastTableData}
              scheduleForecastLoading={scheduleForecastLoading}
              dloScheduleSetting={dloScheduleSetting}
              forecastDSDTableData={forecastDSDTableData}
              forecastNONDSDTableData={forecastNONDSDTableData}
              forecastDSDZoneTableData={forecastDSDZoneTableData}
              forecastNONDSDZoneTableData={forecastNONDSDZoneTableData}
              historicalHoursTableData={historicalHoursTableData}
              historicalHoursData={historicalHoursData}
              historicalHoursLoading={historicalHoursLoading}
              shiftTimesLoading={shiftTimesLoading}
              forecastDSDUpdateLoading={forecastDSDUpdateLoading}
              forecastNONDSDUpdateLoading={forecastNONDSDUpdateLoading}
              forecastDSDZoneUpdateLoading={forecastDSDZoneUpdateLoading}
              forecastNONDSDZoneUpdateLoading={forecastNONDSDZoneUpdateLoading}
              historicalHoursUpdateLoading={historicalHoursUpdateLoading}
              shiftTimesUpdateLoading={shiftTimesUpdateLoading}
              forecastDSDZoneSaved={forecastDSDZoneSaved}
              forecastNONDSDZoneSaved={forecastNONDSDZoneSaved}
              handlers={handlers}
              styles={styles}
            />
          ) : (
            <ActualVolumeTab
              dloScheduleSetting={dloScheduleSetting}
              actualDSDTableData={actualDSDTableData}
              actualNONDSDTableData={actualNONDSDTableData}
              actualDSDZoneTableData={actualDSDZoneTableData}
              actualNONDSDZoneTableData={actualNONDSDZoneTableData}
              actualDSDUpdateLoading={actualDSDUpdateLoading}
              actualNONDSDUpdateLoading={actualNONDSDUpdateLoading}
              actualDSDZoneUpdateLoading={actualDSDZoneUpdateLoading}
              actualNONDSDZoneUpdateLoading={actualNONDSDZoneUpdateLoading}
              actualDSDZoneSaved={actualDSDZoneSaved}
              actualNONDSDZoneSaved={actualNONDSDZoneSaved}
              handlers={handlers}
              styles={styles}
            />
          )}
        </View>
      </View>

      {/* Bottom Create Schedule Button */}
      <View style={styles.bottomButtonContainer}>
        <DLOButton
          text="Create Schedule"
          variant="primary"
          size="medium"
          iconLeading={<FileUploadIcon color="#FFFFFF" size={20} />}
          onPress={handleBackToCreateSchedule}
          disabled={!isAllDataLoaded}
        />
      </View>

      {/* Modal for success/error messages */}
      <DLOModal
        visible={modalState.visible}
        onClose={handleModalClose}
        title={modalState.title}
        primaryButtonText="OK"
        secondaryButtonText=""
        onPrimaryPress={handleModalClose}
        hideButtons={false}
        showCloseIcon={true}
      >
        <Text style={styles.modalMessage}>{modalState.message}</Text>
      </DLOModal>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  breadcrumbContainer: {
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#E9ECEF',
  },
  content: {
    flex: 1,
  },
  headerContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    marginTop: 16,
    paddingHorizontal: 16,
    gap: 16,
  },
  pageTitle: {
    fontSize: 24,
    fontWeight: '600',
    color: '#212529',
    flex: 1,
  },
  tabNavigation: {
    marginBottom: 0,
  },
  tabContent: {
    flex: 1,
    paddingTop: 20,
  },
  tableSpacing: {
    marginTop: 20,
  },
  modalMessage: {
    fontSize: 16,
    color: '#3A3A3A',
    lineHeight: 24,
    textAlign: 'center',
    marginVertical: 10,
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Regular',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
  },
  actionsContainer: {
    flexDirection: 'row',
    gap: 12,
    flexShrink: 0,
  },
  bottomButtonContainer: {
    paddingHorizontal: 16,
    paddingVertical: 16,
    // backgroundColor: '#FFFFFF',
    // borderTopWidth: 1,
    // borderTopColor: '#E9ECEF',
    alignItems: 'flex-end',
  },
});

export default DLOScheduleSetting;
